import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, Collapse, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useState } from 'react';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../../../i18next';
import {
  UkgGuidance,
  UkgTransportCheck,
  UkgVerificationResult,
  toUkgGuidance
} from './verification';

const TransportList = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(1),
  padding: p.theme.spacing(2),
  border: p.theme.custom.border.standard,
  borderRadius: p.theme.custom.border.radius()
}));

const TransportRow = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: p.theme.spacing(1)
}));

const TransportText = styled('div')({
  flex: 1
});

const ProviderError = styled('div')((p) => ({
  fontFamily: `'Courier', mono`,
  fontSize: p.theme.custom.fontSize.s,
  color: p.theme.custom.colors.gray.w900,
  backgroundColor: p.theme.custom.colors.gray.w100,
  padding: p.theme.spacing(1),
  marginTop: p.theme.spacing(1),
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word'
}));

const ToggleRow = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start'
});

/**
 * Headline copy per rest/soap combination. The ok/failed split is not cosmetic:
 * REST authenticates with the username, password and Customer API key, while
 * SOAP additionally needs the User API key — so "reads work, writes don't"
 * points at exactly one credential, and that is the case admins hit most.
 */
const guidanceCopy = (
  guidance: UkgGuidance,
  t: UnsafeTFunction
): { severity: 'success' | 'error'; title: string; body?: string } => {
  switch (guidance) {
    case UkgGuidance.CONNECTED:
      return {
        severity: 'success',
        title: t('Connected'),
        body: t('Reading from and writing back to UKG Pro both work.')
      };
    case UkgGuidance.BOTH_FAILED:
      return {
        severity: 'error',
        title: t('Could not connect to UKG Pro'),
        body: t(
          'Check the Service URL, username, password, and Customer API Key.'
        )
      };
    case UkgGuidance.WRITES_FAILED:
      return {
        severity: 'error',
        title: t("Reads work, writes don't — check the User API Key."),
        body: t(
          'The User API Key comes from the Service Account admin page in UKG, not the Web Services page the Customer API Key is on. It is the only credential the read connection never uses, which is why reads still succeeded.'
        )
      };
    case UkgGuidance.READS_FAILED:
    default:
      return {
        severity: 'error',
        title: t('Unexpected result: writes authenticated but reads did not.'),
        body: t(
          'This is not a combination the UKG credential model explains. Both provider responses are shown below — please send them to support.'
        )
      };
  }
};

const TransportStatus = ({
  label,
  description,
  check
}: {
  label: string;
  description: string;
  check: UkgTransportCheck;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  return (
    <TransportRow>
      {check.ok ? (
        <CheckCircle2 size={18} color={theme.custom.colors.green.w600} />
      ) : (
        <XCircle size={18} color={theme.custom.colors.red.w600} />
      )}
      <TransportText>
        <Typography variant="body2">
          <strong>{label}</strong> — {description}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {check.ok ? t('Working') : t('Failed')}
        </Typography>
      </TransportText>
    </TransportRow>
  );
};

/**
 * Renders a dual-transport verification result: a headline mapped to the
 * specific rest/soap combination, the two transports reported independently,
 * and the RAW provider message behind a disclosure so it is never swallowed
 * into a generic "connection failed".
 */
export const VerificationResultView = ({
  result
}: {
  result: UkgVerificationResult;
}) => {
  const { t } = useUnsafeTranslation();
  const guidance = toUkgGuidance(result);
  const copy = guidanceCopy(guidance, t);

  const hasProviderErrors = !!(result.rest.error || result.soap.error);
  // The rest-failed/soap-ok case has no actionable explanation, so the raw
  // responses ARE the answer — show them without making the admin hunt.
  const [showErrors, setShowErrors] = useState(
    guidance === UkgGuidance.READS_FAILED
  );

  return (
    <>
      <Alert severity={copy.severity}>
        <strong>{copy.title}</strong>
        {copy.body && (
          <Typography variant="body2" component="div">
            {copy.body}
          </Typography>
        )}
      </Alert>

      <TransportList>
        <TransportStatus
          label={t('REST')}
          description={t('reading employee data from UKG')}
          check={result.rest}
        />
        <TransportStatus
          label={t('SOAP')}
          description={t('writing data back to UKG')}
          check={result.soap}
        />

        {hasProviderErrors && (
          <>
            <ToggleRow>
              <Button
                size="small"
                color="primary"
                onClick={() => setShowErrors((s) => !s)}
              >
                {showErrors
                  ? t('Hide the response from UKG')
                  : t('Show the response from UKG')}
              </Button>
            </ToggleRow>
            <Collapse in={showErrors}>
              {result.rest.error && (
                <>
                  <Typography variant="caption" color="textSecondary">
                    {t('REST')}
                  </Typography>
                  <ProviderError>{result.rest.error}</ProviderError>
                </>
              )}
              {result.soap.error && (
                <>
                  <Typography variant="caption" color="textSecondary">
                    {t('SOAP')}
                  </Typography>
                  <ProviderError>{result.soap.error}</ProviderError>
                </>
              )}
            </Collapse>
          </>
        )}
      </TransportList>
    </>
  );
};
